/**
 * Veltrona — live search suggestions (Shopify Predictive Search API).
 */
(() => {
  'use strict';

  const V = window.Veltrona;
  const cache = new Map();

  class VtPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-panel]');
      this.results = this.querySelector('[data-results]');
      this.defaults = this.querySelector('[data-default]');
      this.reset = this.querySelector('button[type="reset"]');
      this.inline = this.classList.contains('search-panel--inline');
      this.enabled = this.dataset.enabled === 'true';
      if (!this.input) return;

      this.onInput = V.utils.debounce(() => this.search(), 220);
      this.input.addEventListener('input', () => {
        this.toggleReset();
        this.onInput();
      });
      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.reset?.addEventListener('click', () => {
        requestAnimationFrame(() => {
          this.input.value = '';
          this.toggleReset();
          this.showDefaults();
          this.input.focus();
        });
      });
      this.form.addEventListener('submit', (e) => {
        if (!this.input.value.trim()) e.preventDefault();
      });
      this.addEventListener('focusout', (e) => {
        if (this.inline && !this.contains(e.relatedTarget)) setTimeout(() => this.close(), 120);
      });
      document.addEventListener('click', (e) => {
        if (this.inline && !this.contains(e.target)) this.close();
      });
      this.toggleReset();
    }

    get term() {
      return this.input.value.trim();
    }

    toggleReset() {
      if (this.reset) this.reset.hidden = !this.input.value;
    }

    open() {
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    showDefaults() {
      this.results.innerHTML = '';
      this.classList.remove('has-results');
      if (this.defaults) this.defaults.hidden = false;
    }

    async search() {
      const term = this.term;
      if (!term) {
        this.showDefaults();
        return;
      }
      if (!this.enabled) return;
      this.open();
      if (cache.has(term)) {
        this.render(cache.get(term));
        return;
      }
      this.abort?.abort();
      this.abort = new AbortController();
      this.classList.add('is-loading');
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query,page,article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });
      try {
        const res = await fetch(`${V.routes.predictiveSearch}?${params}`, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#predictive-search-results');
        html?.removeAttribute('id');
        const markup = html ? html.outerHTML : '';
        cache.set(term, markup);
        if (term === this.term) this.render(markup);
      } catch (e) {
        if (e.name !== 'AbortError') this.close();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(markup) {
      if (this.defaults) this.defaults.hidden = true;
      this.results.innerHTML = markup;
      this.classList.add('has-results');
      this.selected = -1;
      // Two search panels can be on the page, so option ids get this panel's prefix.
      const prefix = this.input?.id || 'ps';
      this.results.querySelectorAll('[role="option"]').forEach((option, i) => {
        option.style.setProperty('--i', Math.min(i, 12));
        if (option.id) option.id = `${prefix}-${option.id}`;
      });
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.inline) {
          this.close();
          this.input.blur();
        } else {
          e.preventDefault();
          this.closest('vt-dialog')?.close();
        }
        return;
      }
      const options = this.options;
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        const current = options[this.selected];
        if (current) {
          e.preventDefault();
          (current.matches('a') ? current : current.querySelector('a'))?.click();
        }
        return;
      }
      e.preventDefault();
      this.selected = e.key === 'ArrowDown' ? (this.selected + 1) % options.length : (this.selected - 1 + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', i === this.selected));
      const active = options[this.selected];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  if (!customElements.get('vt-predictive-search')) customElements.define('vt-predictive-search', VtPredictiveSearch);

  // Focus the dialog search field whenever the search dialog opens.
  document.addEventListener('dialog:open', (e) => {
    const input = e.target.querySelector?.('.search-panel__input');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 50);
  });
})();
